import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '@components/drawer';
import { ConfirmModalV2 } from '@components/modals';
import {
  ADMISSION_DRAWER_OVERLAY_Z_INDEX,
  ADMISSION_DRAWER_SURFACE_CLASSNAME,
  ADMISSION_DRAWER_WIDTH_CLASSES,
} from '@constants/admission-drawer';

import { type OfferFormDrawerProps } from './offer-form-drawer.type';

// Shared drawer shell for the offer chassis forms — owns the unsaved-changes guard + discard-confirm;
// the form is the render-prop child, wired to requestClose / setHasUnsavedChanges.
export const OfferFormDrawer = ({
  isOpen,
  onClose,
  onAfterClose,
  isSaving = false,
  isEditing,
  galleryController,
  testId,
  children,
}: OfferFormDrawerProps): JSX.Element => {
  const { t } = useTranslation('offers');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  // The gallery controller is owned by the page, so its staged state neither participates in the
  // form's RHF dirty tracking nor dies with the drawer — B2B-2078 leaked both ways. The shell wraps
  // the controller's three mutators to learn when the *user* edited the gallery, as opposed to the
  // page seeding it (create-from-preset, duplicate, or the edit re-snapshot), and hands the wrapped
  // controller to the form through the child API.
  const [isGalleryTouched, setIsGalleryTouched] = useState(false);

  const touch = <TArgs extends unknown[]>(mutate: (...args: TArgs) => void) => {
    return (...args: TArgs) => {
      setIsGalleryTouched(true);
      mutate(...args);
    };
  };

  const trackedGalleryController = galleryController && {
    ...galleryController,
    upload: touch(galleryController.upload),
    delete: touch(galleryController.delete),
    reorder: touch(galleryController.reorder),
  };

  useEffect(() => {
    if (isOpen) setIsGalleryTouched(false);
  }, [isOpen]);

  /**
   * Mirrors RHF's `isDirty` for a state system RHF can't see. Both halves are load-bearing:
   * `isGalleryTouched` alone would prompt after an upload the user removed again, and
   * `hasChanges()` alone would prompt on a seeded gallery nobody touched — a create-from-preset
   * session differs from the persisted snapshot from its very first render.
   */
  const hasGalleryChanges = (): boolean => isGalleryTouched && !!galleryController?.hasChanges();

  const requestClose = () => {
    if (isSaving || !isOpen || isDiscardConfirmOpen) return;
    if (hasUnsavedChanges || hasGalleryChanges()) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    onClose();
  };

  // Deferred to `afterClose` so the gallery doesn't visibly empty out mid slide-out. Safe to run
  // after a save too: `use-gallery-save.ts` captures `bulkSnapshot` / `executeFlush` synchronously
  // before its first `await`, and `reset()` only revokes the blob URLs — the captured snapshot holds
  // the `File` objects, so revoking can't starve an upload that is already in flight.
  const handleAfterClose = () => {
    galleryController?.reset();
    setIsGalleryTouched(false);
    onAfterClose?.();
  };

  const handleDiscardConfirm = () => {
    setIsDiscardConfirmOpen(false);
    onClose();
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={requestClose}
        onAfterClose={handleAfterClose}
        position='right'
        bodyScrollable={false}
        closeOnBackdropClick={!isSaving}
        className={ADMISSION_DRAWER_WIDTH_CLASSES}>
        <div data-testid={testId} className='h-full'>
          {children({ requestClose, setHasUnsavedChanges, galleryController: trackedGalleryController })}
        </div>
      </Drawer>

      <ConfirmModalV2
        isOpen={isDiscardConfirmOpen}
        layout='dialog'
        type='warning'
        closeOnBackdropClick={false}
        title={t('offers:admissionModalConfirmTitle')}
        description={
          isEditing
            ? t('offers:admissionEditModalConfirmDescription')
            : t('offers:admissionCreationModalConfirmDescription')
        }
        onConfirm={handleDiscardConfirm}
        onCancel={() => setIsDiscardConfirmOpen(false)}
        surfaceClassName={ADMISSION_DRAWER_SURFACE_CLASSNAME}
        zIndex={ADMISSION_DRAWER_OVERLAY_Z_INDEX}
      />
    </>
  );
};
